/**
 * PricingPage — "Tarification des analyses": assign/update the lab's price per analysis type over the M7
 * catalogue price ledger. A first price INSTANTIATES the lab's ParametreLabo overlay; an existing one is
 * a LEDGERED change (motif MANDATORY — append-only CataloguePriceHistory, fiscal/medico-legal). The
 * cockpit never edits a price in place silently; it delegates verbatim to the catalogue service.
 *
 * Gating: read+write on catalogue.parametre.price (the owner-scoped trio) — screen ForbiddenState +
 * nav-item cap. Money is integer centimes on the wire; the form edits the major DZD unit. Full 5-state DoD.
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  Modal,
  OfflineState,
  Table,
  Textarea,
  useToast,
  type Column,
} from "@/ui";
import { ApiError } from "@/api";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { CatalogueTypeahead } from "@/features/catalogue/CatalogueTypeahead";
import { useParametreSearch } from "@/features/catalogue/api";
import type { ParametreHit } from "@/features/catalogue/types";
import { ADMIN_CAPS } from "../caps";
import { useAssignPrice, useLabId, usePricing } from "../api";
import type { PricingRow } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { centimesToEditable, formatCentimes, parseToCentimes } from "../lib";

export function PricingPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canPrice = useCan(ADMIN_CAPS.pricing);

  const query = usePricing(labId, canPrice && online);
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const [search, setSearch] = useState("");
  // undefined = modal closed; null = new (free code); a row = edit existing.
  const [editing, setEditing] = useState<PricingRow | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.parametreCode.toLowerCase().includes(q) || r.libelle.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const columns: ReadonlyArray<Column<PricingRow>> = [
    {
      key: "code",
      header: t("admin.pricing.col.code"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.parametreCode,
      cell: (r) => <span className="nv-ltr-island">{r.parametreCode}</span>,
    },
    { key: "libelle", header: t("admin.pricing.col.libelle"), sortable: true, sortAccessor: (r) => r.libelle, cell: (r) => r.libelle },
    { key: "discipline", header: t("admin.pricing.col.discipline"), mono: true, cell: (r) => <span className="nv-ltr-island">{r.disciplineCode ?? "—"}</span> },
    {
      key: "prix",
      header: t("admin.pricing.col.prix"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.prix,
      cell: (r) => <span className="nv-ltr-island">{formatCentimes(r.prix)}</span>,
    },
    {
      key: "statut",
      header: t("admin.pricing.col.statut"),
      cell: (r) =>
        r.cacheStatut === "retire" ? (
          <Badge tone="warn" glyph="⊘">
            {t("admin.pricing.statut.retire")}
          </Badge>
        ) : (
          <Badge tone="success" glyph="●">
            {t("admin.pricing.statut.actif")}
          </Badge>
        ),
    },
    {
      key: "actions",
      header: t("admin.pricing.col.actions"),
      align: "end",
      cell: (r) => (
        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
          {t("admin.pricing.edit")}
        </Button>
      ),
    },
  ];

  const header = (
    <PageHeader
      title={t("admin.pricing.title")}
      subtitle={t("admin.pricing.subtitle")}
      actions={<Button onClick={() => setEditing(null)}>{t("admin.pricing.assign")}</Button>}
    />
  );

  if (!canPrice) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.pricing]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)", borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))" }}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.pricing.search")}
            aria-label={t("admin.pricing.search")}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.parametreLaboId}
            caption={t("admin.pricing.title")}
            pageSize={12}
            emptyTitle={search.trim() ? t("admin.pricing.empty.search") : t("admin.pricing.empty.title")}
            {...(search.trim() ? {} : { emptyDescription: t("admin.pricing.empty.desc") })}
            emptyAction={
              !search.trim() ? <Button onClick={() => setEditing(null)}>{t("admin.pricing.assign")}</Button> : undefined
            }
          />
        </div>
      </Card>

      <AssignPriceModal
        open={editing !== undefined}
        row={editing ?? null}
        labId={labId}
        onClose={() => setEditing(undefined)}
      />
    </Screen>
  );
}

function AssignPriceModal({
  open,
  row,
  labId,
  onClose,
}: {
  open: boolean;
  row: PricingRow | null;
  labId: string | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const assign = useAssignPrice(labId);
  const isEdit = !!row;

  const [code, setCode] = useState("");
  const [prix, setPrix] = useState("");
  const [motif, setMotif] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seed the form when the modal opens for a given target.
  const seedKey = row?.parametreLaboId ?? (open ? "_new" : "_closed");
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setCode(row?.parametreCode ?? "");
    setPrix(row ? centimesToEditable(row.prix) : "");
    setMotif("");
    setError(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  const close = () => {
    onClose();
  };

  const submit = () => {
    setError(null);
    const parametreCode = (isEdit ? row!.parametreCode : code).trim();
    if (!parametreCode) {
      setError(t("admin.field.required"));
      return;
    }
    const centimes = parseToCentimes(prix);
    if (centimes === null) {
      setError(t("admin.field.invalidAmount"));
      return;
    }
    // A change of an EXISTING overlay requires a motif (the server enforces; we pre-validate for UX).
    if (isEdit && !motif.trim()) {
      setError(t("admin.pricing.motif.required"));
      return;
    }
    assign.mutate(
      {
        parametre_code: parametreCode,
        prix: centimes,
        ...(motif.trim() ? { motif: motif.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(
            res.mode === "instantiated"
              ? t("admin.pricing.saved.instantiated")
              : t("admin.pricing.saved.changed"),
          );
          close();
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? t("admin.pricing.modal.title.edit", { code: row!.parametreCode }) : t("admin.pricing.modal.title.new")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("admin.action.cancel")}
          </Button>
          <Button onClick={submit} loading={assign.isPending}>
            {t("admin.pricing.modal.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField label={t("admin.pricing.modal.code")} required>
          {() =>
            isEdit ? (
              <Input
                value={row!.parametreCode}
                disabled
                className="nv-ltr-island"
                aria-label={t("admin.pricing.modal.code")}
              />
            ) : code ? (
              // A code is already picked — show it with a "change" affordance (clear → re-search).
              <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
                <Badge tone="neutral">
                  <span className="nv-ltr-island nv-mono">{code}</span>
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => setCode("")}>
                  {t("admin.action.cancel")}
                </Button>
              </div>
            ) : (
              <CatalogueTypeahead<ParametreHit>
                useSearch={useParametreSearch}
                mono
                placeholder={t("admin.pricing.modal.code.placeholder")}
                aria-label={t("admin.pricing.modal.code")}
                getKey={(r) => r.code}
                onSelect={(r) => setCode(r.code)}
                renderItem={(r) => (
                  <>
                    <span className="nv-ltr-island nv-mono">{r.code}</span>
                    <span style={{ flex: "1 1 auto" }}>{r.libelle}</span>
                    {r.disciplineCode && (
                      <Badge tone="neutral">
                        <span className="nv-ltr-island nv-mono">{r.disciplineCode}</span>
                      </Badge>
                    )}
                  </>
                )}
              />
            )
          }
        </FormField>
        <FormField label={t("admin.pricing.modal.prix")} required>
          {(p) => (
            <Input
              {...p}
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
              inputMode="decimal"
              className="nv-ltr-island"
            />
          )}
        </FormField>
        {isEdit && (
          <FormField label={t("admin.pricing.modal.motif")} hint={t("admin.pricing.modal.motif.hint")} required>
            {(p) => <Textarea {...p} value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} />}
          </FormField>
        )}
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
